import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SmsFailedIcon from "@mui/icons-material/SmsFailed";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { apiErrorDetail } from "../../lib/api";
import {
  UnmatchedSms,
  dismissUnmatchedSms,
  listUnmatchedSms,
  resolveUnmatchedSms,
} from "../../services/smsAdminService";

// TRAIN-4 · the unmatched SMS queue, surfaced. Every row here is a REAL text
// from a REAL phone the dispatcher could not attribute — an unknown renter,
// an ambiguous number, or an unparseable sender. D6: these are customers,
// never silently dropped; this panel is where a human resolves them.

export default function UnmatchedSmsQueue() {
  const [rows, setRows] = useState<UnmatchedSms[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [clientIdDrafts, setClientIdDrafts] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<string | null>(null);
  // The backend refuses a phone mismatch ONCE and names the client; we show
  // exactly that before the operator can commit. Filing a stranger's text into
  // someone's thread must never happen silently from a pasted UUID.
  const [mismatch, setMismatch] = useState<
    { rowId: string; clientId: string; message: string } | null
  >(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setRows(await listUnmatchedSms());
    } catch (err: any) {
      setError(err?.message || "Failed to load unmatched messages");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (rowId: string, fn: () => Promise<any>, done: string) => {
    setBusyId(rowId);
    setError(null);
    try {
      const result = await fn();
      setRows((prev) => (prev || []).filter((r) => r.id !== rowId));
      setNotice(
        result?.consent_recorded === "carrier_stop"
          ? `${done} — STOP recorded in the consent ledger`
          : done
      );
    } catch (err: any) {
      const detail = apiErrorDetail(err);
      if (typeof detail === "object" && detail?.reason === "phone_mismatch") {
        setMismatch({
          rowId,
          clientId: (clientIdDrafts[rowId] || "").trim(),
          message: detail.message,
        });
        return;
      }
      setError(
        (typeof detail === "object" && detail?.message) ||
          (typeof detail === "string" && detail) ||
          err?.message ||
          "Action failed"
      );
    } finally {
      setBusyId(null);
    }
  };

  if (rows === null && !error) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size={22} data-testid="unmatched-sms-loading" />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 2, mb: 3 }} data-testid="unmatched-sms-queue">
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <SmsFailedIcon fontSize="small" color="warning" />
        <Typography variant="subtitle1" fontWeight={600}>
          Unmatched texts
        </Typography>
        <Chip size="small" label={rows?.length ?? 0} />
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        Texts to the LocateNow number from phones that didn't match exactly one
        client. Paste a client ID to file a message into their thread, or
        dismiss spam/wrong numbers.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} data-testid="unmatched-sms-error">
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 1 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      {rows && rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Queue is empty — every inbound text is attributed.
        </Typography>
      ) : (
        (rows || []).map((row) => (
          <Box
            key={row.id}
            data-testid={`unmatched-row-${row.id}`}
            sx={{ borderTop: "1px solid", borderColor: "divider", py: 1.25 }}
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Chip size="small" variant="outlined" label={row.from_phone} />
              {/* PKT-0472 · a recognised LEAD must not be labelled "no match".
                  This chip used to render every non-'ambiguous' reason as
                  "no match", so the dispatcher could identify a lead, record
                  their id, text the operators "see Unmatched texts in LN Pro
                  admin" — and the operator would arrive at a row calling that
                  lead a stranger. The queue is where the human acts, so being
                  right only in the database is being wrong. */}
              <Chip
                size="small"
                color={
                  row.reason === "ambiguous"
                    ? "warning"
                    : row.reason === "lead_match"
                    ? "info"
                    : "default"
                }
                label={
                  row.reason === "ambiguous"
                    ? "matches several clients"
                    : row.reason === "lead_match"
                    ? "known lead"
                    : "no match"
                }
              />
              {/* PKT-0471 A2 · "1 media" reads as "there is an attachment
                  here", so an operator may go looking for a photo that was
                  never retained — the webhook records num_media and nothing
                  else. A true-looking row creating a false belief is worse
                  than a blank one, because a blank row at least announces
                  that it is empty. Say the image is gone. */}
              {row.num_media > 0 && (
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={`📎 ${row.num_media} ${
                    row.num_media === 1 ? "image" : "images"
                  } (not stored)`}
                />
              )}
              {row.created_at && (
                <Typography variant="caption" color="text.secondary">
                  {new Date(row.created_at).toLocaleString()}
                </Typography>
              )}
            </Stack>
            <Typography variant="body2" sx={{ my: 0.75, whiteSpace: "pre-wrap" }}>
              {row.body || <em>(no text — media only)</em>}
            </Typography>
            {row.reason === "ambiguous" && row.matched_client_ids && (
              <Typography variant="caption" color="text.secondary" display="block">
                Candidates: {row.matched_client_ids.join(" · ")}
              </Typography>
            )}
            {row.reason === "lead_match" && row.matched_lead_ids && (
              <Typography variant="caption" color="text.secondary" display="block">
                Lead{row.matched_lead_ids.length > 1 ? "s" : ""}:{" "}
                {row.matched_lead_ids.join(" · ")}
              </Typography>
            )}
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              <TextField
                size="small"
                placeholder="client id"
                value={clientIdDrafts[row.id] || ""}
                onChange={(e) =>
                  setClientIdDrafts((d) => ({ ...d, [row.id]: e.target.value }))
                }
                inputProps={{ "data-testid": `resolve-client-input-${row.id}` }}
                sx={{ width: 320 }}
              />
              <Button
                size="small"
                variant="contained"
                disabled={busyId === row.id || !(clientIdDrafts[row.id] || "").trim()}
                data-testid={`resolve-button-${row.id}`}
                onClick={() =>
                  act(
                    row.id,
                    () => resolveUnmatchedSms(row.id, clientIdDrafts[row.id].trim()),
                    "Message filed into the client's thread"
                  )
                }
              >
                File to client
              </Button>
              <Button
                size="small"
                color="inherit"
                disabled={busyId === row.id}
                data-testid={`dismiss-button-${row.id}`}
                onClick={() =>
                  act(row.id, () => dismissUnmatchedSms(row.id), "Dismissed")
                }
              >
                Dismiss
              </Button>
            </Stack>
          </Box>
        ))
      )}
      <Dialog
        open={mismatch !== null}
        onClose={() => setMismatch(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Different phone number</DialogTitle>
        <DialogContent>
          <DialogContentText data-testid="mismatch-message">
            {mismatch?.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMismatch(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            data-testid="mismatch-confirm"
            onClick={() => {
              const m = mismatch;
              setMismatch(null);
              if (m) {
                act(
                  m.rowId,
                  () => resolveUnmatchedSms(m.rowId, m.clientId, true),
                  "Message filed into the client's thread"
                );
              }
            }}
          >
            File it anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
